import React, { useEffect, useState } from "react"
import {
  Card,
  CardBody,
  Label,
  Input,
  FormFeedback,
  Button,
  Table,
  Spinner,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchNodeData,
  FetchNodeDeleteData,
  FetchNodePostData,
  FetchNodePutData,
} from "utils/common-fetchData"

const getFinancialYears = () => {
  const years = []
  const end = new Date().getFullYear() + 1
  for (let y = 2020; y <= end; y++) {
    years.push(`${y}-${y + 1}`)
  }
  return years
}

const FINANCIAL_YEARS = getFinancialYears()

const EMPTY_LUT = { financialYear: "", lutNumber: "" }

const validateLut = data => {
  const e = {}
  if (!data.financialYear) e.financialYear = "Please select a financial year."
  if (!data.lutNumber?.trim()) e.lutNumber = "LUT number is required."
  return e
}

const LutRecords = () => {
  const [lutList, setLutList] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [lutForm, setLutForm] = useState({ ...EMPTY_LUT })
  const [lutErrors, setLutErrors] = useState({})
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)

  const token = localStorage.getItem("auth_token") // adjust if needed

  // ✅ LOAD DATA
  const loadLut = async () => {
    const res = await fetchNodeData(null, "invoiceDetails/lut", token)
    if (res?.success) {
      const formatted = res.data.map(i => ({
        id: i.id,
        financialYear: i.financial_year,
        lutNumber: i.lut_number,
      }))
      console.log("formatted", formatted)
      setLutList(formatted)
    }
  }

  useEffect(() => {
    loadLut()
  }, [])

  const openAdd = () => {
    setEditingId(null)
    setLutForm({ ...EMPTY_LUT })
    setLutErrors({})
    setShowForm(true)
  }

  const openEdit = item => {
    setEditingId(item.id)
    setLutForm({
      financialYear: item.financialYear,
      lutNumber: item.lutNumber,
    })
    setLutErrors({})
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingId(null)
  }

  const handleField = (name, value) => {
    setLutForm(prev => ({ ...prev, [name]: value }))
    if (lutErrors[name]) setLutErrors(prev => ({ ...prev, [name]: undefined }))
  }

  // ✅ SAVE (CREATE / UPDATE)
  const handleSave = async () => {
    const errs = validateLut(lutForm)
    if (Object.keys(errs).length) {
      setLutErrors(errs)
      return
    }

    setSaving(true)

    const payload = {
      financial_year: lutForm.financialYear,
      lut_number: lutForm.lutNumber,
    }

    try {
      let res

      if (editingId) {
        res = await FetchNodePutData(
          setSaving,
          "invoiceDetails/lut",
          editingId,
          payload
        )
      } else {
        res = await FetchNodePostData(
          setSaving,
          "invoiceDetails/lut",
          token,
          payload
        )
      }

      if (res?.data?.success) {
        Swal.fire("Success", res.data.message, "success")
        await loadLut()
        closeForm()
      } else {
        Swal.fire(
          "Error",
          res?.response?.data?.message || "Something went wrong",
          "error"
        )
      }
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  // ✅ DELETE
  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This LUT record will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    }).then(async result => {
      if (result.isConfirmed) {
        const res = await FetchNodeDeleteData(
          setSaving,
          "invoiceDetails/lut",
          id
        )

        if (res?.data?.success) {
          Swal.fire("Deleted!", res.data.message, "success")
          loadLut()
        } else {
          Swal.fire("Error", "Failed to delete", "error")
        }
      }
    })
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0">LUT Records</h5>
            <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
              Financial year &amp; LUT numbers
            </p>
          </div>
          <button
            type="button"
            className={`btn btn-sm ${
              showForm ? "btn-outline-danger" : "btn-purple"
            }`}
            onClick={showForm ? closeForm : openAdd}
          >
            {showForm ? (
              "Close"
            ) : (
              <>
                {" "}
                <i className="fa fa-plus me-2"></i> Add New{" "}
              </>
            )}
          </button>
        </div>

        {showForm && (
          <div className="border rounded p-3 mb-3 bg-light">
            <p
              className="fw-semibold mb-3 text-purple"
              style={{ fontSize: "0.85rem" }}
            >
              {editingId ? "Edit Record" : "New Record"}
            </p>

            <div className="row">
              <div className="col-5 mb-3">
                <Label>
                  Financial Year <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={lutForm.financialYear}
                  onChange={e => handleField("financialYear", e.target.value)}
                  invalid={!!lutErrors.financialYear}
                >
                  <option value="">Select Year</option>
                  {FINANCIAL_YEARS.map(fy => (
                    <option key={fy} value={fy}>
                      {fy}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{lutErrors.financialYear}</FormFeedback>
              </div>

              <div className="col-7 mb-3">
                <Label>
                  LUT Number <span className="text-danger">*</span>
                </Label>
                <Input
                  placeholder="Enter LUT Number"
                  value={lutForm.lutNumber}
                  onChange={e => handleField("lutNumber", e.target.value)}
                  invalid={!!lutErrors.lutNumber}
                />
                <FormFeedback>{lutErrors.lutNumber}</FormFeedback>
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <Button color="danger" outline size="sm" onClick={closeForm}>
                Close
              </Button>
              <Button
                className="btn-purple"
                size="sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Saving…
                  </>
                ) : editingId ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        )}

        {lutList.length === 0 ? (
          <div className="text-center text-muted py-4">
            <p className="mb-1">No records found.</p>
            <small>Click "+ Add New" to create one.</small>
          </div>
        ) : (
          <Table bordered hover responsive size="sm" className="mb-0">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Financial Year</th>
                <th>LUT Number</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lutList.map((item, idx) => (
                <tr
                  key={item.id}
                  className={editingId === item.id ? "table-primary" : ""}
                >
                  <td>{idx + 1}</td>
                  <td>
                    <span className="badge bg-info bg-soft text-info font-size-12">
                      {item.financialYear}
                    </span>
                  </td>
                  <td>{item.lutNumber}</td>
                  <td className="text-center">
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      className="me-1"
                      onClick={() => openEdit(item)}
                    >
                      <i className="fa fa-pen" />
                    </Button>
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                    >
                      <i className="fa fa-trash" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  )
}

export default LutRecords
